import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import theme from '../../../theme';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { appServiceLines, commonEnums, commonEnums as enums } from '@codametrix/ui-common';
import * as colDefs from './case-list-columns';
import { filterCaseList } from './case-list-columns';
import { DataGrid } from '../../../components/data-grid/data-grid';
import { styles } from './case-list.styles';
import { Divider, Grid, GridItem, Text } from '@chakra-ui/react';
import { caseListEndPoint } from './case-list-utils';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { PopUpFiltersV2 } from '../../pop-up-filter/pop-up-filtersV2';
import { useDispatch, useSelector } from 'react-redux';
import { RowsPerPage } from '../../../components/rows-per-page/rows-per-page';
import { fetchCaseListColumnConfiguration } from '../../../actions/ui';
import { configurationUtils } from '@codametrix/ui-common';
const { FilterTypes } = enums;
const INITIAL_PAGE_SIZE = 100;
const gridOptions = {
    suppressHorizontalScroll: true,
    sortingOrder: [
        'asc',
        'desc',
        null
    ],
    paginationPageSizeSelector: false
};
const CaseList = (props)=>{
    const { serviceLine, sortablePageable, criteriaFilter, activeOrgId, filterLoadStates, list, getDictionary, getStatuses, getTenants, onSelection, clearFilters, context, showOrderNumber } = props;
    const dispatch = useDispatch();
    const tenantId = useSelector((state)=>state.ui.context.activeContext?.healthSystemTenantId);
    const showOrderNumberFilter = useSelector((state)=>{
        return state.cases.showOrderNumber;
    });
    const [filterState, setFilterState] = useState({
        config: null,
        selectedOption: '',
        isLoaded: false
    });
    const [searchText, setSearchText] = useState('');
    const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
    const [filterableOptions, setFilterableOptions] = useState(sortablePageable.filterableOptions);
    useEffect(()=>{
        const orderNumberServiceLines = new Set([
            commonEnums.ServiceLines.RADIOLOGY,
            commonEnums.ServiceLines.PATHOLOGY
        ]);
        if (context?.activeContext?.healthSystemTenantId && serviceLine?.name && orderNumberServiceLines.has(serviceLine.name) && showOrderNumber) {
            showOrderNumber('order_number_caselist', context.activeContext.healthSystemTenantId);
        }
    }, [
        serviceLine,
        showOrderNumber,
        context
    ]);
    // Fetch and process filter configuration in a single effect
    useEffect(()=>{
        const fetchAndProcessFilterConfig = async ()=>{
            if (!tenantId || !serviceLine?.name) {
                setFilterState((prev)=>({
                        ...prev,
                        isLoaded: true
                    }));
                return;
            }
            try {
                const jsonValue = await configurationUtils.fetchMergedConfigurationJsonValue('case-list-filter-options', tenantId, serviceLine.name);
                const config = jsonValue;
                // Filter out order number options if flag is false
                const filteredOptions = config.availableOptions?.filter((option)=>{
                    if (!showOrderNumberFilter) {
                        return option.key !== 'placer_order_number';
                    }
                    return true;
                }) || [];
                const processedConfig = {
                    ...config,
                    availableOptions: filteredOptions
                };
                // Determine the selected option
                let selectedOption = '';
                const defaultOption = filteredOptions.find((option)=>option.key === config.defaultFilterOption);
                if (defaultOption) {
                    selectedOption = defaultOption.label;
                } else if (filteredOptions.length > 0) {
                    selectedOption = filteredOptions[0].label;
                }
                setFilterState({
                    config: processedConfig,
                    selectedOption,
                    isLoaded: true
                });
            } catch (error) {
                console.error('Error fetching filter configuration:', error);
                setFilterState((prev)=>({
                        ...prev,
                        isLoaded: true
                    }));
            }
        };
        fetchAndProcessFilterConfig();
    }, [
        tenantId,
        serviceLine?.name,
        showOrderNumberFilter
    ]);
    // Memoized search forms - derived from filter state
    const searchForms = useMemo(()=>{
        return filterState.config?.availableOptions?.map((option)=>({
                label: option.label,
                value: option.key,
                type: option.type
            })) || [];
    }, [
        filterState.config
    ]);
    const selectOptionsRef = useRef(searchForms);
    useEffect(()=>{
        selectOptionsRef.current = searchForms;
    }, [
        searchForms
    ]);
    useEffect(()=>{
        if (tenantId && serviceLine?.name) {
            dispatch(fetchCaseListColumnConfiguration({
                tenantId,
                serviceLine: serviceLine.name
            }));
        }
    }, [
        dispatch,
        tenantId,
        serviceLine?.name
    ]);
    const columnConfig = useSelector((state)=>state.ui.caseListColumnConfig);
    const columns = useMemo(()=>{
        return columnConfig ? Object.entries(columnConfig).map(([key, config])=>{
            const predefinedColDef = colDefs[config.field];
            if (!predefinedColDef) {
                return {
                    field: config.field,
                    headerName: config.headerName,
                    sortable: false
                };
            }
            return {
                ...predefinedColDef,
                headerName: config.headerName || predefinedColDef.headerName
            };
        }) : [];
    }, [
        columnConfig
    ]);
    const handleRowSelection = (item)=>{
        if (item.data) {
            onSelection(item.data);
        }
    };
    const activeContextOrg = useSelector((state)=>{
        return state.ui.context.activeContext?.organizationName;
    });
    const classes = styles();
    const serviceLineConfig = serviceLine ? appServiceLines.determineServiceLineConfig(serviceLine) : null;
    const generateNewFilterList = ()=>{
        const newFilterableOptions = {};
        let newFilterList = [];
        // Use the selected option from filter configuration
        const selectedOptionConfig = filterState.config?.availableOptions?.find((option)=>option.label === filterState.selectedOption);
        const selectedOptionKey = selectedOptionConfig?.key || '';
        newFilterList = criteriaFilter.filters.filter((filter)=>{
            return filter.type === FilterTypes.IN;
        });
        // Use dynamic config type
        const filterType = selectedOptionConfig?.type || FilterTypes.EQ;
        if (searchText.trim() && selectedOptionKey) {
            const newFilter = {
                key: selectedOptionKey,
                terms: [
                    searchText.trim()
                ],
                type: filterType
            };
            newFilterList.push(newFilter);
        }
        newFilterableOptions.filters = newFilterList;
        return newFilterableOptions;
    };
    const handleSearchOptionChange = (event)=>{
        setFilterState((prev)=>({
                ...prev,
                selectedOption: event?.target?.textContent
            }));
    };
    const handleSearchInputChange = (event)=>{
        setSearchText(event?.target?.value);
    };
    const handleSearchKeyPress = (event)=>{
        if (event?.keyCode === 13) {
            const filterableOption = generateNewFilterList();
            setFilterableOptions(filterableOption);
        }
    };
    useEffect(()=>{
        setFilterableOptions(sortablePageable.filterableOptions);
    }, [
        sortablePageable.filterableOptions
    ]);
    const onPageSizeChange = (pageSize)=>{
        setPageSize(pageSize);
    };
    // Early return if serviceLine is not available yet or filter config is loading
    if (!serviceLine || !serviceLineConfig || !filterState.isLoaded) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    return /*#__PURE__*/ _jsxs("div", {
        style: classes.root,
        children: [
            /*#__PURE__*/ _jsx("div", {
                style: classes.header,
                children: /*#__PURE__*/ _jsx(Text, {
                    sx: classes.text,
                    children: "Cases"
                })
            }),
            /*#__PURE__*/ _jsxs(Grid, {
                flexDirection: 'row',
                gridTemplateRows: 'min-content min-content min-content',
                gap: theme.space[12],
                style: classes.content,
                children: [
                    /*#__PURE__*/ _jsx(GridItem, {
                        children: /*#__PURE__*/ _jsx(SearchBar, {
                            selectOptions: searchForms,
                            selectedOption: filterState.selectedOption,
                            onSelectOptionChange: handleSearchOptionChange,
                            onSearchInputChange: handleSearchInputChange,
                            onSearchKeyPress: handleSearchKeyPress
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        children: /*#__PURE__*/ _jsx(Divider, {
                            sx: classes.divider
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        children: /*#__PURE__*/ _jsxs(Grid, {
                            display: 'flex',
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            children: [
                                /*#__PURE__*/ _jsx(PopUpFiltersV2, {
                                    criteriaFilter: criteriaFilter,
                                    loadStates: filterLoadStates,
                                    sortablePageable: sortablePageable,
                                    list: list,
                                    getDictionary: getDictionary,
                                    getStatuses: getStatuses,
                                    getTenants: getTenants,
                                    clearFilters: clearFilters,
                                    activeOrgId: activeOrgId,
                                    setFilterableOptions: setFilterableOptions,
                                    isCases: true
                                }),
                                /*#__PURE__*/ _jsx(RowsPerPage, {
                                    pageSize: pageSize,
                                    handlePageSize: onPageSizeChange
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(GridItem, {
                        children: /*#__PURE__*/ _jsx(DataGrid, {
                            onRowClicked: handleRowSelection,
                            columnDefs: columns,
                            endpointData: caseListEndPoint(serviceLineConfig.caselistAltId || serviceLineConfig.amplifyPath, serviceLineConfig.caseListVersion),
                            filterableOptions: filterableOptions,
                            serverSideInfiniteScroll: true,
                            gridOptions: gridOptions,
                            contentFilter: filterCaseList,
                            loaderInfo: `Just a moment while we collect ${activeContextOrg} cases with your selected criteria`,
                            paginationPageSize: pageSize
                        })
                    })
                ]
            })
        ]
    });
};
export { CaseList };
